import { type PreventivoDTO } from '@framework/api-client/api/backoffice/schemas/preventivoDTO.ts';
import {
  getGetFrontRichiesteIdPreventiviQueryKey,
  usePutFrontRichiesteIdPreventiviIdAccettazione,
} from '@framework/api-client/api/inquiries-compile/preventivi-richiesta/preventivi-richiesta.ts';
import {
  getGetFrontRichiesteIdPagamentiQueryKey,
  getGetFrontRichiesteIdQueryKey,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { ConfirmButton } from '@framework/ui/components/button/ConfirmButton.tsx';
import { AlignRow } from '@framework/ui/components/row/AlignRow.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { message, Space, type TableColumnsType, Tooltip } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { type ISubmissionQuote } from './ISubmissionQuote.ts';
import { QuoteDownloadButton } from './QuoteDownloadButton.tsx';
import { quoteColumns } from './quoteColumns.tsx';
import { SubmissionQuoteLogic } from './SubmissionQuoteLogic.ts';

interface IQuoteTableProps {
  readonly?: boolean;
  quotes?: ISubmissionQuote[];
  submissionId: string | null | undefined;
}

export function QuoteTable(props: IQuoteTableProps) {
  const { readonly = false, quotes, submissionId } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const accept = usePutFrontRichiesteIdPreventiviIdAccettazione();

  const acceptQuote = useCallback(
    async (record: PreventivoDTO, toAccept: boolean) => {
      try {
        await accept.mutateAsync({
          id: submissionId ?? '<unknown>',
          data: { accetta: toAccept },
          idPreventivo: record.id ?? '<unknown>',
        });
        // biome-ignore format: <tbd>
        await queryClient.invalidateQueries(getGetFrontRichiesteIdPreventiviQueryKey(submissionId ?? '<unknown>'));
        // biome-ignore format: <tbd>
        await queryClient.invalidateQueries(getGetFrontRichiesteIdQueryKey(submissionId ?? '<unknown>'));
        // biome-ignore format: <tbd>
        await queryClient.invalidateQueries(getGetFrontRichiesteIdPagamentiQueryKey(submissionId ?? '<unknown>'));
      } catch (e) {
        // biome-ignore format: <tbd>
        console.error(`[QuoteTable] Impossibile accettare il preventivo`, { e });
        // biome-ignore format: <tbd>
        message.error(getNetworkErrorMessage(e, t("quote-table.errore-in-accettazione-preventivo", 'Si è verificato un problema durante l\'accettazione del preventivo')));
      }
    },
    [accept, queryClient, submissionId],
  );

  if (!quotes) {
    return null;
  }

  return (
    <TableData
      size="small"
      pagination={false}
      dataSource={quotes}
      expandable={
        readonly
          ? undefined
          : {
              showExpandColumn: quotes.some(q =>
                SubmissionQuoteLogic.requiresAcceptance(q),
              ),
              rowExpandable: record =>
                SubmissionQuoteLogic.requiresAcceptance(record),
              // expandRowByClick: true,
              defaultExpandAllRows: true,
              expandedRowRender: record =>
                !SubmissionQuoteLogic.requiresAcceptance(record) ? undefined : (
                  <div>
                    <AlignRow justify="flex-end">
                      <Space>
                        <ConfirmButton
                          size="large"
                          buttonDanger
                          onConfirm={async () =>
                            await acceptQuote(record, false)
                          }
                          loading={accept.isLoading}
                          title={t(
                            'quote-table.azioni.button-confirm.refuse.message.rifiuto-preventivo',
                            'Sei sicuro di voler rifiutare il Preventivo?',
                          )}
                        >
                          {t(
                            'quote-table.azioni.button-confirm.refuse.text',
                            'RIFIUTA',
                          )}
                        </ConfirmButton>
                        <ConfirmButton
                          size="large"
                          popconfirmButtonDanger={false}
                          onConfirm={async () =>
                            await acceptQuote(record, true)
                          }
                          loading={accept.isLoading}
                          title={
                            <Space direction="vertical" size={0}>
                              <div>
                                {t(
                                  'quote-table.azioni.button-confirm.accept.message.accetta-preventivo-prima-riga',
                                  'Confermi di aver scaricato e letto il il preventivo nella sua interezza.',
                                )}
                              </div>
                              <div>
                                {t(
                                  'quote-table.azioni.button-confirm.accept.message.accetta-preventivo-seconda-riga',
                                  'Confermi di voler accettare il preventivo?',
                                )}
                              </div>
                            </Space>
                          }
                        >
                          {t(
                            'quote-table.azioni.button-confirm.accept.text',
                            'ACCETTA',
                          )}
                        </ConfirmButton>
                      </Space>
                    </AlignRow>
                  </div>
                ),
            }
      }
      columns={
        [
          ...quoteColumns,
          {
            title: t('quote-table.azioni', 'Azioni'),
            key: 'actions',
            align: 'right',
            hidden: readonly,
            render: (_, record) => (
              <Space>
                <Tooltip
                  title={t(
                    'quote-table.azioni.button.tooltips.scarica-preventivo',
                    'Scarica Preventivo',
                  )}
                >
                  <QuoteDownloadButton
                    submissionId={submissionId ?? '<unknown>'}
                    quoteId={record.id ?? '<unknown>'}
                  />
                </Tooltip>
              </Space>
            ),
          },
        ] satisfies TableColumnsType<ISubmissionQuote>
      }
    />
  );
}
